import React, { useEffect, useRef, useState } from 'react'
import Input from 'components/input'
import { useDataContext } from 'components/document/contexts/data'
import { getSectionDisplayNumber } from 'components/document/helpers/formatting'
import useProject from 'hooks/useProject'
import { IHeadingStyle, IProcessSectionObject } from 'types'
import { CollapseAll, Cross, ExpandAll, Move, Restore, Triangle } from 'components/icons'
import { scrollToSection, sectionSearchMatch } from 'components/document/helpers/functions'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'

interface ProcessSectionTitleProps {
  section: IProcessSectionObject
  sectionNumber: string
  level: number
  formatting: IHeadingStyle
}

const ProcessSectionTitle: React.FC<ProcessSectionTitleProps> = ({ section, sectionNumber, level, formatting }) => {
  const { project } = useProject()
  const { searchTerm, sectionVisibility, sectionExpanded, setSectionExpanded } = useDataContext()

  const isSectionVisible = sectionVisibility[section.publicId] ?? false
  const isExpanded = sectionExpanded[section.publicId] ?? false

  useEffect(() => {
    if (searchTerm !== '' && !isExpanded && sectionSearchMatch(section, searchTerm)) {
      setSectionExpanded(section.publicId, true)
    }
  }, [searchTerm])

  return (
    <div className="w-full cursor-pointer select-none">
      <div
        className={`flex w-full ${isSectionVisible && 'bg-light-blue'} transition-all pr-5`}
        style={{ paddingLeft: `${level * 10 + 10}px` }}
      >
        <span
          className="transition-all pt-2.5 pb-2.5 pl-[5px] pr-[5px]"
          title={isExpanded ? 'Collapse Section' : 'Expand Section'}
          onClick={() => setSectionExpanded(section.publicId, !isExpanded)}
        >
          <Triangle type={isExpanded ? 'down' : 'right'} className="h-2.5 w-2.5" />
        </span>
        <span
          className="transition-all w-full truncate pt-2.5 pb-2.5 pl-[5px]"
          title={`Scroll To ${section.name}`}
          onClick={() => scrollToSection(section.publicId)}
        >
          {sectionNumber}&nbsp;
          {section.name}
        </span>
      </div>
      {isExpanded &&
        section.children.map((subsection: IProcessSectionObject, subsectionNumber: number) => {
          const subsectionFormatting = project.headingStyles[level] ? project.headingStyles[level] : formatting
          const subsectionDisplayNumber = getSectionDisplayNumber(sectionNumber, subsectionNumber, subsectionFormatting)
          const display = sectionSearchMatch(subsection, searchTerm)
          if (display)
            return (
              <ProcessSectionTitle
                key={subsection.publicId}
                section={subsection}
                sectionNumber={subsectionDisplayNumber}
                level={level + 1}
                formatting={subsectionFormatting}
              />
            )
        })}
    </div>
  )
}

const Navigation: React.FC = () => {
  const { project } = useProject()
  const { process, expandAllSections, collapseAllSections, searchTerm, setSearchTerm } = useDataContext()

  const [y, setY] = useState<number>()
  const [x, setX] = useState<number>()
  const [height, setHeight] = useState<number>()
  const [width, setWidth] = useState<number>()

  const DEFAULT_X = 10
  const DEFAULT_Y = 55
  const DEFAULT_HEIGHT = 400
  const DEFAULT_WIDTH = 362

  const formatting = project.headingStyles[0]
    ? project.headingStyles[0]
    : {
        fontSize: 1.4,
        colour: project.processTitleColour,
        bold: project.processTitleBold,
        italic: project.processTitleItalic,
        underline: project.processTitleUnderline
      }

  const dragging = useRef<boolean>(false)
  const mouseStartX = useRef<number | undefined>(undefined)
  const mouseStartY = useRef<number | undefined>(undefined)
  const originalX = useRef<number | undefined>(undefined)
  const originalY = useRef<number | undefined>(undefined)

  useEffect(() => {
    setX(DEFAULT_X)
    setY(DEFAULT_Y)
    setHeight(DEFAULT_HEIGHT)
    setWidth(DEFAULT_WIDTH)

    originalX.current = x
    originalY.current = y
    mouseStartX.current = x
    mouseStartY.current = y

    return () => {
      setSearchTerm('')
    }
  }, [])

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault()
    dragging.current = true
    mouseStartX.current = event.clientX
    mouseStartY.current = event.clientY
  }

  const handleMouseMove = (event: MouseEvent) => {
    if (
      dragging.current === true &&
      mouseStartX.current &&
      mouseStartY.current &&
      originalX.current &&
      originalY.current
    ) {
      const deltaX = event.clientX - mouseStartX.current
      const deltaY = event.clientY - mouseStartY.current
      const newX = originalX.current - deltaX
      const newY = originalY.current + deltaY
      setX(newX)
      setY(newY)
    } else if (verticalDragging.current === true && verticalMouseStart.current && originalHeight.current) {
      const deltaY = event.clientY - verticalMouseStart.current
      const newHeight = originalHeight.current + deltaY
      setHeight(newHeight)
    } else if (horizontalDragging.current === true && horizontalMouseStart.current && originalWidth.current) {
      const deltaX = event.clientX - horizontalMouseStart.current
      const newWidth = originalWidth.current - deltaX
      setWidth(newWidth)
    }
  }

  const handleMouseUp = () => {
    if (dragging.current === true) {
      originalX.current = x
      originalY.current = y
      dragging.current = false
      mouseStartX.current = 0
      mouseStartY.current = 0
    } else if (verticalDragging.current === true) {
      verticalDragging.current = false
    } else if (horizontalDragging.current === true) {
      horizontalDragging.current = false
    }
  }

  // Vertical resize logic
  const verticalDragging = useRef<boolean>(false)
  const verticalMouseStart = useRef<number | undefined>(undefined)
  const originalHeight = useRef<number | undefined>(undefined)

  const handleVerticalMouseDown = (event: React.MouseEvent) => {
    verticalDragging.current = true
    originalHeight.current = height ? height : DEFAULT_HEIGHT
    verticalMouseStart.current = event.clientY
  }

  // Horizontal resize logic
  const horizontalDragging = useRef<boolean>(false)
  const horizontalMouseStart = useRef<number | undefined>(undefined)
  const originalWidth = useRef<number | undefined>(undefined)

  const handleHorizontalMouseDown = (event: React.MouseEvent) => {
    horizontalDragging.current = true
    originalWidth.current = width ? width : DEFAULT_WIDTH
    horizontalMouseStart.current = event.clientX
  }

  const searchRef = useRef<HTMLInputElement>(null)
  const searchTimeout = useRef<TimeoutID | null>(null)

  const updateSearch = (search: string) => {
    if (searchTimeout.current !== null) cancelTimeout(searchTimeout.current)
    searchTimeout.current = requestTimeout(() => {
      setSearchTerm(search)
    }, 500)
  }

  const resetNavPosition = () => {
    const x = DEFAULT_X
    const y = DEFAULT_Y

    dragging.current = false
    mouseStartX.current = 0
    mouseStartY.current = 0
    originalX.current = x
    originalY.current = y
    setX(x)
    setY(y)
  }

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [x, y, height, width])

  if (x !== undefined && y !== undefined && height !== undefined && width !== undefined) {
    return (
      <div
        className="flex flex-col rounded shadow bg-surface select-none rounded fixed z-[2]"
        style={{ inset: `${y}px ${x}px auto auto`, width: `${width}px`, height: `${height}px`, borderBottom: 'none' }}
      >
        <div className="flex items-center border-b-1 border-divider font-bold">
          <Input
            ref={searchRef}
            className="m-2.5"
            placeholder="Search for section"
            defaultValue={''}
            onDragStart={(event) => event.preventDefault()}
            onChange={(event) => updateSearch(event.target.value)}
          />
          <div
            className="flex items-center justify-center hover:bg-surface-sunken transition-all cursor-pointer p-[7px] w-[30px]"
            title="Clear Search"
            onClick={() => {
              if (searchRef.current) {
                searchRef.current.value = ''
                setSearchTerm('')
              }
            }}
          >
            <Cross />
          </div>

          <div
            className="flex items-center justify-center hover:bg-surface-sunken transition-all cursor-pointer p-[7px] w-[30px]"
            title="Expand All Sections"
            onClick={() => {
              if (process) {
                expandAllSections()
              }
            }}
          >
            <ExpandAll />
          </div>
          <div
            className="flex items-center justify-center hover:bg-surface-sunken transition-all cursor-pointer p-[7px] w-[30px]"
            title="Collapse All Sections"
            onClick={() => {
              if (process) {
                collapseAllSections()
              }
            }}
          >
            <CollapseAll />
          </div>
          <div
            className="flex items-center justify-center hover:bg-surface-sunken transition-all cursor-pointer p-[7px] w-[30px]"
            title="Reset Navigation Position"
            onClick={() => {
              resetNavPosition()
            }}
          >
            <Restore />
          </div>
          <div
            className="flex items-center justify-center cursor-grab hover:bg-surface-sunken transition-all p-[7px] mr-2.5 w-[30px]"
            title="Move Navigation Panel"
            onMouseDown={handleMouseDown}
          >
            <Move />
          </div>
        </div>
        <div className="overflow-y-auto ">
          {process &&
            process.children.map((section: IProcessSectionObject, sectionNumber: number) => {
              const sectionDisplayNumber = getSectionDisplayNumber('', sectionNumber, formatting)
              const display = sectionSearchMatch(section, searchTerm)
              if (display)
                return (
                  <ProcessSectionTitle
                    key={sectionNumber}
                    level={1}
                    sectionNumber={sectionDisplayNumber}
                    section={section}
                    formatting={formatting}
                  />
                )
            })}
        </div>
        <div
          className="absolute w-full cursor-ns-resize block rounded bg-blue opacity-0 hover:opacity-100 h-1.5 bottom-0"
          onMouseDown={handleVerticalMouseDown}
        />
        <div
          className="absolute cursor-ew-resize block rounded bg-blue opacity-0 hover:opacity-100 left-0 w-1.5"
          style={{ height: `${height}px` }}
          onMouseDown={handleHorizontalMouseDown}
        />
      </div>
    )
  } else return <div />
}

export default Navigation
